import { io } from 'socket.io-client';
import config from '../config';
import { clearAuthStorage, stopSessionGuard } from '../utils/sessionGuard';

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.lastSequence = 0;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.authFailed = false; // 인증 실패 플래그 추가
    this.intentionalDisconnect = false; // 의도적 연결 해제 플래그 추가
    this.isForceLogoutProcessing = false; // 강제 로그아웃 처리 중 플래그
  }

  // 소켓 연결
  connect() {
    // 새 연결 시도 시 강제 로그아웃 플래그 리셋
    this.isForceLogoutProcessing = false;

    console.log('🔌 소켓 연결 시도 시작');

    if (this.socket && this.connected) {
      console.log('✅ 이미 연결됨');
      return;
    }
    
    // 인증 실패했거나 의도적으로 연결을 끊은 경우 재연결하지 않음
    if (this.authFailed || this.intentionalDisconnect) {
      console.log('🚫 소켓 연결 차단: 인증 실패 또는 의도적 연결 해제');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.log('🚫 소켓 연결 차단: 토큰 없음');
      return;
    }
    
    console.log('🔌 소켓 연결 설정:', config.SOCKET_URL);

    // 통합 API 서버로 연결
    this.socket = io(config.SOCKET_URL, {
      transports: ['websocket', 'polling'],  // WebSocket 우선, polling 폴백
      auth: {
        token
      },
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000
    });

    // 연결 이벤트
    this.socket.on('connect', () => {
      // [2026-08-07] 연결(재연결 포함) 신호 — 채팅 위젯이 끊김 동안 놓친 메시지를 재조회하는 트리거
      window.dispatchEvent(new CustomEvent('socketConnected'));
      console.log('✅ 웹소켓 연결됨, Socket ID:', this.socket.id);
      this.connected = true;
      this.reconnectAttempts = 0;
      
      // 인증 (유저 타입 명시)
      this.socket.emit('authenticate', { 
        token,
        type: 'user'
      });
      console.log('📤 authenticate 이벤트 전송됨');
      
      // 재연결 시 누락된 이벤트 요청
      if (this.lastSequence > 0) {
        this.socket.emit('request-missing-events', {
          lastSequence: this.lastSequence
        });
      }
    });
    
    // 연결 에러 이벤트
    this.socket.on('connect_error', (error) => {
      console.error('❌ 웹소켓 연결 에러:', error.message);
      this.reconnectAttempts++;
      console.log(`🔄 재연결 시도: ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
    });

    // 연결 해제 이벤트
    this.socket.on('disconnect', (reason) => {
      console.log('❌ 웹소켓 연결 해제됨:', reason);
      this.connected = false;
      
      // 인증 실패나 의도적 연결 해제인 경우 재연결 방지
      if (this.authFailed || this.intentionalDisconnect) {
        console.log('🚫 재연결 차단: 인증 실패 또는 의도적 연결 해제');
        this.socket.io.opts.reconnection = false;
      }
    });

    // 인증 결과
    this.socket.on('authenticated', (data) => {
      if (data.success) {
        console.log('✅ 웹소켓 인증 성공');
        this.authFailed = false; // 인증 성공 시 플래그 초기화
      } else {
        console.error('❌ 웹소켓 인증 실패:', data.error);
        this.authFailed = true; // 인증 실패 플래그 설정
        this.intentionalDisconnect = true; // 의도적 연결 해제 플래그 설정
        this.disconnect();
        
        // JWT 만료 시 토큰 제거 및 로그인 페이지로 리디렉션
        /* [2026-09-07] 정리 규칙을 utils/sessionGuard.js 한 곳으로 모은다(활동 기록·타이머 포함).
           WHY: 여기서만 token·userId 를 지우면 잔액·포인트가 남아 화면이 로그인 상태처럼 보인다. */
        if (data.error && data.error.includes('jwt expired')) {
          stopSessionGuard();
          clearAuthStorage();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      }
    });

    // 누락된 이벤트 수신
    this.socket.on('missing-events', (data) => {
      console.log(`📥 누락된 이벤트 ${data.count}개 수신`);
      data.events.forEach(event => {
        this.handleEvent(event);
      });
    });

    // 실시간 이벤트 리스너 등록
    this.registerEventListeners();

    // 전역 접근을 위해 window 객체에 저장
    window.socket = this.socket;
  }

  // 실시간 이벤트 리스너 등록
  registerEventListeners() {
    // 회원 관련 이벤트
    this.socket.on('member:balance-changed', this.handleEvent.bind(this));
    this.socket.on('member:status-changed', this.handleEvent.bind(this));
    
    // 잔액 실시간 업데이트 이벤트 (서버에서 발생하는 이벤트들)
    this.socket.on('balance:changed', this.handleBalanceUpdate.bind(this));
    this.socket.on('balance:update', this.handleBalanceUpdate.bind(this));
    this.socket.on('member:balance:updated', this.handleBalanceUpdate.bind(this));
    
    // 입출금 관련 이벤트
    this.socket.on('deposit:approved', this.handleEvent.bind(this));
    this.socket.on('deposit:rejected', this.handleEvent.bind(this));
    this.socket.on('withdrawal:approved', this.handleEvent.bind(this));
    this.socket.on('withdrawal:rejected', this.handleEvent.bind(this));
    
    // 베팅 관련 이벤트
    this.socket.on('bet:settled', this.handleEvent.bind(this));
    
    // 롤링금(포인트) 관련 이벤트
    this.socket.on('rolling:update', this.handleRollingUpdate.bind(this));
    this.socket.on('rolling:updated', this.handleRollingUpdate.bind(this)); // 하위 호환성
    // [2026-10-28] "포인트"가 바뀌는데 회원 화면이 받지 않던 알림 — 새로고침 전까지 옛 값이었다. 롤링 알림과 같은 처리기(본인 확인 → pointsUpdated)로 넘겨 useBalance 가 다시 읽게 한다.
    //   'point:changed' 관리자 포인트 지급·회수(관리 API memberAdjustment.js) · 'point:update' 낙첨 포인트 적립(관리 API losingPointService.js)
    //   · 'rolling:revoked' 롤링금 회수(rollingCalculator — sportsRollingCalculator 의 회수는 스포츠 롤링만 깎아 "포인트"는 그대로지만 다시 읽어도 해가 없다)
    this.socket.on('point:changed', this.handleRollingUpdate.bind(this));
    this.socket.on('point:update', this.handleRollingUpdate.bind(this));
    this.socket.on('rolling:revoked', this.handleRollingUpdate.bind(this));
    
    // 시스템 관련 이벤트
    this.socket.on('system:announcement', this.handleEvent.bind(this));
    this.socket.on('system:maintenance', this.handleEvent.bind(this));
    
    // [2026-08-07] 실시간 상담 채팅 이벤트 — 위젯(ChatWidget)이 window 이벤트로 구독한다.
    //   기존 message:* 와 같은 전파 방식(CustomEvent) 재사용. 정본: docs/채팅기능_구현_정본.md
    //   서버 발신 헬퍼(sendToUser)는 페이로드를 {eventName, data, ...} 로 감싸 보낸다(실측) —
    //   위젯이 쓰기 좋게 안쪽 data 만 꺼내 전파한다(감싸지 않은 형태가 와도 그대로 통과).
    const unwrapChat = (raw) => (raw && raw.data !== undefined ? raw.data : raw);
    this.socket.on('chat:message', (raw) => {
      window.dispatchEvent(new CustomEvent('chat:message', { detail: unwrapChat(raw) }));
    });
    this.socket.on('chat:closed', (raw) => {
      window.dispatchEvent(new CustomEvent('chat:closed', { detail: unwrapChat(raw) }));
    });
    this.socket.on('chat:read', (raw) => {
      window.dispatchEvent(new CustomEvent('chat:read', { detail: unwrapChat(raw) }));
    });

    // 메시지 관련 이벤트
    this.socket.on('message:new', this.handleMessageEvent.bind(this));
    this.socket.on('message:reply', this.handleMessageEvent.bind(this));
    this.socket.on('message:status_change', this.handleMessageEvent.bind(this));
    this.socket.on('message:read', this.handleMessageEvent.bind(this));
    
    // 공지사항 관련 이벤트
    this.socket.on('notice:new', this.handleNoticeEvent.bind(this));
    this.socket.on('notice:update', this.handleNoticeEvent.bind(this));
    this.socket.on('notice:delete', this.handleNoticeEvent.bind(this));
    this.socket.on('new_notice', this.handleNoticeEvent.bind(this));  // 관리자 API에서 발생하는 이벤트
    this.socket.on('user_new_notice', this.handleNoticeEvent.bind(this));
    this.socket.on('notice_read', this.handleNoticeEvent.bind(this));  // 읽음 처리 이벤트

    // 회원레벨 변경 이벤트 (관리자가 레벨 변경 시 수신)
    this.socket.on('member-level:changed', this.handleMemberLevelChanged.bind(this));

    // 중복 로그인 강제 로그아웃 처리
    this.socket.on('force-logout', this.handleForceLogout.bind(this));

    // [Sports WebSocket] 스포츠 실시간 데이터 이벤트
    this.socket.on('sports:odds:update', this.handleSportsOddsUpdate.bind(this));
    this.socket.on('sports:score:update', this.handleSportsScoreUpdate.bind(this));
    this.socket.on('sports:match:status', this.handleSportsMatchStatus.bind(this));
    this.socket.on('sports:market:status', this.handleSportsMarketStatus.bind(this));
    // [2026-10-18 W93] 손등록(수동) 경기 배당·마켓 변경 알림 — 받으면 화면이 그 경기 자료를 다시 받는다(SportsPage·SportsMatchList·BettingSlipContext)
    this.socket.on('sports:manual:odds', this.handleSportsManualOdds.bind(this));
    this.socket.on('sports:settlement', this.handleSportsSettlement.bind(this));

    // 스포츠 룸 참가/탈퇴 응답
    this.socket.on('sports:joined', this.handleSportsJoined.bind(this));
    this.socket.on('sports:left', this.handleSportsLeft.bind(this));
    this.socket.on('sports:error', this.handleSportsError.bind(this));
  }

  // 이벤트 처리
  handleEvent(event) {
    // 시퀀스 업데이트
    if (event.sequence) {
      this.lastSequence = event.sequence;
      this.socket.emit('update-sequence', { sequence: event.sequence });
    }
    
    // 이벤트별 처리
    console.log(`📥 이벤트 수신: ${event.eventName}`, event.data);
  }

  // 잔액 업데이트 처리
  handleBalanceUpdate(eventWrapper) {
    console.log('💰 실시간 잔액 업데이트:', eventWrapper);
    
    // eventWrapper가 { eventName, data, timestamp, sequence } 형태인 경우와 직접 데이터인 경우를 구분
    const data = eventWrapper.data || eventWrapper;
    
    // 현재 로그인한 유저의 잔액인지 확인
    const currentUserId = localStorage.getItem('userId');
    const dataUserId = data.userId || data.memberId;
    
    if (dataUserId && String(dataUserId) === String(currentUserId)) {
      // 다양한 형태의 balance 필드 지원
      // WHY: `||` 연산자는 0을 falsy로 처리해 sports 베팅 후 balance=0 이벤트를 무시함.
      //      nullish coalescing(??)으로 바꿔 0 값도 정확히 전파되도록 수정.
      const balance = data.balance ?? data.newBalance ?? data.afterBalance;
      
      // 잔액 업데이트 이벤트 발생
      // [WHY] point 필드 detail 에 추가 (2026-05-06)
      //   admin-api 가 입금 승인·환전 거절 emit 페이로드에 members.point 를 동봉하기 시작했다.
      //   기존에는 detail 에 gameMoney 만 있어 충전보너스 포인트가 새로고침 전까지 갱신되지 않았다.
      //   [2026-10-21 정정] useBalance 는 입금·출금 알림(type deposit·withdrawal·withdrawal_refund)을 이 값 대신 DB 기준으로
      //   다시 읽는다 — 이 값은 회원 포인트 칸뿐(롤링금 빠짐)이라 화면 "포인트"(롤링금 + 회원 포인트)와 달랐다. 전달은 그대로 둔다(쓰는 곳 없음).
      const event = new CustomEvent('balanceUpdated', {
        detail: {
          balance: balance,
          gameMoney: data.gameMoney,  // 스포츠 머니
          point: data.point,           // 포인트(보너스 등)
          changeAmount: data.changeAmount || 0,
          type: data.type || data.source,
          // [2026-10-28] 관리자 '롤링전환' 알림은 type 없이 action('rolling_transfer')만 싣는다 — useBalance 가 이 값을 보고 다시 읽는다
          action: data.action,
          timestamp: data.timestamp || new Date()
        }
      });
      window.dispatchEvent(event);
      
      // localStorage에도 업데이트 (옵션)
      if (balance !== undefined) {
        localStorage.setItem('userBalance', balance);
      }
      
      // 롤링금(포인트)도 함께 업데이트되는 경우
      if (data.rollingAmount !== undefined) {
        const pointEvent = new CustomEvent('pointsUpdated', { 
          detail: { 
            points: data.rollingAmount,
            changeAmount: 0,
            type: 'sync',
            timestamp: data.timestamp || new Date()
          } 
        });
        window.dispatchEvent(pointEvent);
        localStorage.setItem('userPoints', data.rollingAmount);
      }
    }
  }

  // 롤링금(포인트) 업데이트 처리
  handleRollingUpdate(eventWrapper) {
    console.log('🎯 실시간 포인트 업데이트 시작');
    console.log('🎯 전체 이벤트:', JSON.stringify(eventWrapper, null, 2));
    
    // eventWrapper가 { eventName, data, timestamp, sequence } 형태인 경우와 직접 데이터인 경우를 구분
    const data = eventWrapper.data || eventWrapper;
    console.log('🎯 처리할 데이터:', data);
    
    // 현재 로그인한 유저의 포인트인지 확인
    const currentUserId = localStorage.getItem('userId');
    const dataUserId = data.userId || data.memberId;
    
    if (dataUserId && String(dataUserId) === String(currentUserId)) {
      // 서버에서 rollingAmount로 전송
      const points = data.rollingAmount || data.point || 0;
      
      // 포인트 업데이트 이벤트 발생
      const event = new CustomEvent('pointsUpdated', { 
        detail: { 
          points: points,
          changeAmount: data.changeAmount || 0,
          type: data.type || 'rolling_accumulated',
          gameType: data.gameType,
          timestamp: data.timestamp || new Date()
        } 
      });
      window.dispatchEvent(event);
      
      // localStorage에도 업데이트 (옵션)
      if (points !== undefined) {
        localStorage.setItem('userPoints', points);
      }
    }
  }

  // 메시지 이벤트 처리
  handleMessageEvent(data) {
    console.log('💬 메시지 이벤트:', data);

    // 메시지 이벤트 전파
    const eventName = data.type || 'messageUpdate';
    const event = new CustomEvent(eventName, { detail: data });
    window.dispatchEvent(event);

    // 특정 이벤트에 대한 추가 처리
    if (data.type === 'message:reply') {
      // 답변 알림용 이벤트도 발생
      window.dispatchEvent(new CustomEvent('messageReply', { detail: data }));
    }

    if (data.type === 'message:read') {
      // 읽음 처리 알림용 이벤트도 발생
      window.dispatchEvent(new CustomEvent('messageRead', { detail: data }));
    }
  }

  // 공지사항 이벤트 처리
  handleNoticeEvent(data) {
    console.log('📢 공지사항 이벤트:', data);

    // 대상 타입 확인 (all 또는 user인 경우만 처리)
    if (data.target && data.target !== 'all' && data.target !== 'user') {
      console.log('🚫 이 공지사항은 유저 페이지에 표시되지 않습니다:', data.target);
      return;
    }

    // 공지사항 이벤트 전파
    const event = new CustomEvent('noticeUpdate', {
      detail: {
        type: data.type || 'new',
        notice: data.notice || data,
        timestamp: data.timestamp || new Date()
      }
    });
    window.dispatchEvent(event);

    // 새 공지사항인 경우 슬라이더 업데이트 이벤트도 발생
    if (data.type === 'notice:new' || data.type === 'new' || !data.type) {
      window.dispatchEvent(new CustomEvent('noticeSliderUpdate', { detail: data }));
    }
  }

  // 회원레벨 변경 이벤트 처리
  handleMemberLevelChanged(data) {
    console.log('⭐ 회원레벨 변경 이벤트:', data);

    // 현재 로그인한 유저의 레벨 변경인지 확인
    const currentUserId = localStorage.getItem('userId');
    const targetMemberId = data.memberId;

    if (targetMemberId && String(targetMemberId) === String(currentUserId)) {
      // 레벨 변경 CustomEvent 발생 (useAuth에서 상태 갱신하도록)
      const event = new CustomEvent('memberLevelChanged', {
        detail: {
          oldLevelId: data.oldLevelId,
          newLevelId: data.newLevelId,
          timestamp: data.timestamp || new Date()
        }
      });
      window.dispatchEvent(event);
    }
  }

  // 중복 로그인 강제 로그아웃 처리
  handleForceLogout(data) {
    console.log('🚨 강제 로그아웃 이벤트 수신:', data);

    // 1. 이미 처리 중이면 무시 (다중 실행 방지)
    if (this.isForceLogoutProcessing) {
      console.log('🔒 강제 로그아웃 이미 처리 중 - 무시');
      return;
    }

    // 2. 현재 세션의 토큰과 비교하여 새 세션이면 무시
    const myToken = localStorage.getItem('token');
    if (data.currentToken && data.currentToken === myToken) {
      console.log('🔒 현재 세션의 토큰과 일치 - force-logout 무시');
      return;
    }

    // 처리 시작 플래그 설정
    this.isForceLogoutProcessing = true;

    // 3. 재연결 비활성화
    if (this.socket) {
      this.socket.io.opts.reconnection = false;
    }

    // 4. 소켓 연결 해제
    this.disconnect();

    // 5. 로컬 스토리지 정리
    /* [2026-09-07] 활동 기록·만료 감시까지 한 번에 정리한다(정리 규칙은 utils/sessionGuard.js 한 곳).
       WHY: 활동 시각이 남으면 다음 로그인이 옛 값을 물려받아 곧바로 무활동으로 판정될 수 있다. */
    stopSessionGuard();
    clearAuthStorage();

    // 6. 사용자에게 알림 표시
    const reason = data?.message || data?.reason || '다른 곳에서 로그인되었습니다.';
    alert(reason);

    // 7. 로그인 페이지로 즉시 리다이렉트
    console.log('🔒 강제 로그아웃 - 로그인 페이지로 이동');
    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  }

  // [Sports WebSocket] 배당 업데이트 처리
  handleSportsOddsUpdate(data) {
    const event = new CustomEvent('sportsOddsUpdate', {
      detail: {
        type: 'odds_update',
        data: data,
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(event);
  }

  // [Sports WebSocket] 스코어 업데이트 처리
  handleSportsScoreUpdate(data) {
    const event = new CustomEvent('sportsScoreUpdate', {
      detail: {
        type: 'score_update',
        data: data,
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(event);
  }

  // [Sports WebSocket] 경기 상태 변경 처리
  handleSportsMatchStatus(data) {
    const event = new CustomEvent('sportsMatchStatus', {
      detail: {
        type: 'match_status',
        data: data,
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(event);
  }

  // [2026-10-18 W93] 손등록 경기 배당·마켓 변경 알림 처리 — 배당 값은 싣지 않는다(회원 서버 응답이 정본이라 다시 받아 그린다)
  handleSportsManualOdds(data) {
    window.dispatchEvent(new CustomEvent('sportsManualOdds', { detail: { type: 'manual_odds', data, timestamp: Date.now() } }));
  }

  // [Sports WebSocket] 마켓 상태 변경 처리
  handleSportsMarketStatus(data) {
    const event = new CustomEvent('sportsMarketStatus', {
      detail: {
        type: 'market_status',
        data: data,
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(event);
  }

  // [Sports WebSocket] 정산 알림 처리
  handleSportsSettlement(data) {
    const event = new CustomEvent('sportsSettlement', {
      detail: {
        type: 'settlement',
        data: data,
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(event);
  }

  // [Sports WebSocket] 룸 참가 성공 처리
  handleSportsJoined(data) {
  }

  // [Sports WebSocket] 룸 탈퇴 성공 처리
  handleSportsLeft(data) {
  }

  // [Sports WebSocket] 스포츠 에러 처리
  handleSportsError(data) {
    console.error('❌ 스포츠 WebSocket 에러:', data);
  }

  // [Sports WebSocket] 스포츠 룸 참가
  joinSportsRoom(room) {
    if (this.socket && this.connected) {
      this.socket.emit('sports:join', { room });
    }
  }

  // [Sports WebSocket] 스포츠 룸 탈퇴
  leaveSportsRoom(room) {
    if (this.socket && this.connected) {
      this.socket.emit('sports:leave', { room });
    }
  }

  // 소켓 연결 해제
  disconnect() {
    if (this.socket) {
      this.intentionalDisconnect = true; // 의도적 연결 해제 플래그 설정
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      window.socket = null;
    }
  }
  
  // 플래그 리셋 (재로그인 시 사용)
  resetFlags() {
    this.authFailed = false;
    this.intentionalDisconnect = false;
    this.reconnectAttempts = 0;
    this.isForceLogoutProcessing = false;
  }

  // 이벤트 리스너 등록
  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  // 이벤트 리스너 제거
  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  // 이벤트 발송
  emit(event, data) {
    if (this.socket && this.connected) {
      this.socket.emit(event, data);
    }
  }

  // 잔액 업데이트 리스너
  onBalanceUpdate(callback) {
    this.on('balanceUpdate', callback);
  }

  // 공지사항 리스너
  onAnnouncement(callback) {
    this.on('announcement', callback);
  }

  // 게임 상태 업데이트 리스너
  onGameStatusUpdate(callback) {
    this.on('gameStatusUpdate', callback);
  }
}

// 싱글톤 인스턴스
const socketService = new SocketService();

export default socketService;